import { WorkTask } from 'api-client';
import { retryUntilCondition } from '@planitgeo/treeplotter-domain/lib/infrastructure/retry/retry-utils';
import WorkTaskRepository from 'common/infrastructure/work-task/repository';
import { WorkTaskDtoWithPhotos } from 'common/infrastructure/work-task/types';

/**
 * Shared retry/backoff tuning for every work-task read-model reconciliation poll — the
 * app-wide sync runner ({@link useWorkTaskSyncRunner}) and the standalone readiness
 * wait ({@link waitForWorkTaskReady}) — so they stay in sync when tuned.
 * ~10 attempts with 300ms × 1.6 backoff.
 */
export const WORK_TASK_RETRY_OPTIONS = {
  maxRetries: 10,
  retryDelay: 300,
  backoffMultiplier: 1.6,
} as const;

/**
 * A work task is "ready" for downstream actions once it exists in the read model AND
 * its `tp2WorkRecordId` is populated. That id is assigned asynchronously a moment after
 * creation, and is required before anything can reference the work record (showing it
 * as actionable, attaching photos, etc.).
 *
 * This is the canonical "is the new work usable yet?" predicate, shared by the
 * background sync poller and the standalone readiness wait.
 */
export function isWorkTaskReady(task: WorkTask): boolean {
  return task.tp2WorkRecordId != null;
}

/**
 * True once every one of `photoIds` is attached to the task in the read model. The
 * `WorkTask` DTO omits `photos` in the spec, but the backend returns it (see
 * {@link WorkTaskDtoWithPhotos}); photo ids are client-minted and preserved, so we
 * match by id. An empty `photoIds` is trivially satisfied.
 */
export function areWorkTaskPhotosAttached(
  task: WorkTaskDtoWithPhotos,
  photoIds: string[]
): boolean {
  if (photoIds.length === 0) return true;
  const attached = new Set((task.photos ?? []).map((photo) => photo.id));
  return photoIds.every((id) => attached.has(id));
}

/**
 * Polls the CQRS read model until a work task is ready (see {@link isWorkTaskReady}),
 * returning true once it is or false if it doesn't become ready within the retry
 * budget. Any flow acting on freshly-created work (e.g. the future attach-photo flow)
 * should await this first. Reuses the shared {@link retryUntilCondition} primitive.
 */
export async function waitForWorkTaskReady(
  tenantId: string,
  workTaskId: string,
  signal?: AbortSignal
): Promise<boolean> {
  const outcome = await retryUntilCondition(
    async () => {
      const found = await WorkTaskRepository.findWorkTasksByIds([workTaskId], tenantId, signal);
      const task = found.find((t) => t.id === workTaskId);
      return !!task && isWorkTaskReady(task);
    },
    (ready) => ready,
    {
      ...WORK_TASK_RETRY_OPTIONS,
      signal,
      logContext: { workTaskId, action: 'work_task_ready_wait' },
    }
  );
  return outcome.success;
}
